import { useProgramReportCard, useProgramNavigation } from '../../../hooks/useProgramReportCard';
import { ReportCardHeader } from './ReportCardHeader';
import { ReportCardNavigation } from './ReportCardNavigation';
import { SectionBlock, FieldRow, QcFlagDisplay, CategoryBuckets } from '../shared';
import type { Category } from '../shared';
import styles from './ProgramReportCard.module.css';

export interface ProgramReportCardProps {
  baciCode: string;
  className?: string;
}

/**
 * ProgramReportCard - Professional read-only report for authors/publishers
 *
 * Features:
 * - Letterhead header with large logo
 * - All 7 program-level tables displayed
 * - QC flags and comments (read-only)
 * - Category auto-bucketing
 * - Print-optimized styling
 * - Alphabetical navigation (Next/Back)
 */
export function ProgramReportCard({ baciCode, className = '' }: ProgramReportCardProps) {
  const { data, isLoading, error } = useProgramReportCard(baciCode);
  const { data: navData } = useProgramNavigation(baciCode);

  // Loading state
  if (isLoading) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>Loading program report card...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.error}>
          <h2>Error Loading Report Card</h2>
          <p>{error.message}</p>
          <a href="#data-quality" className={styles.backButton}>
            ← Back to Data Quality
          </a>
        </div>
      </div>
    );
  }

  // No data state
  if (!data) {
    return (
      <div className={`${styles.container} ${className}`}>
        <div className={styles.empty}>
          <h2>Program Not Found</h2>
          <p>No data available for BACI code: {baciCode}</p>
          <a href="#data-quality" className={styles.backButton}>
            ← Back to Data Quality
          </a>
        </div>
      </div>
    );
  }

  // Navigation handlers
  const handlePrevious = () => {
    if (navData?.previousProgram) {
      window.location.hash = `#program-card-v2?program_code=${navData.previousProgram.baci_program_code}`;
    }
  };

  const handleNext = () => {
    if (navData?.nextProgram) {
      window.location.hash = `#program-card-v2?program_code=${navData.nextProgram.baci_program_code}`;
    }
  };

  // Transform categories for CategoryBuckets component
  const categories: Category[] = (data.categories || []).map((cat) => ({
    id: cat.id,
    name: cat.name
  }));

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Navigation */}
      {navData && (
        <ReportCardNavigation
          currentProgram={baciCode}
          onPrevious={handlePrevious}
          onNext={handleNext}
          hasPrevious={navData.hasPrevious}
          hasNext={navData.hasNext}
          currentIndex={navData.currentIndex}
          totalPrograms={navData.total}
        />
      )}

      {/* Header */}
      <ReportCardHeader
        programName={data.program_name}
        baciCode={data.baci_program_code}
        organization={data.awarding_organization}
        foundingYear={data.founding_year}
        email={data.contact_email}
        phone={data.contact_phone}
        website={data.website_url}
      />

      {/* Main Content */}
      <main className={styles.content}>
        {/* 1. CONTACTS SECTION */}
        <SectionBlock title="Contacts" icon="📋">
          <FieldRow label="Contact Name" value={data.contact_name} />
          <FieldRow label="Contact Email" value={data.contact_email} />
          <FieldRow label="Contact Phone" value={data.contact_phone} />
          <FieldRow label="Physical Address" value={data.physical_address} />
          <FieldRow label="Contact Notes" value={data.contact_notes} multiline />

          <QcFlagDisplay
            flagged={data.qc_flag_contacts}
            notes={data.qc_flag_contacts_notes}
            editable={false}
            okLabel="No Major Issues (blocking points) for Contacts"
          />
        </SectionBlock>

        {/* 2. DEADLINES SECTION */}
        <SectionBlock title="Deadlines" icon="📅">
          <FieldRow label="Early Bird Deadline" value={data.early_bird_deadline} />
          <FieldRow label="Regular Deadline" value={data.regular_deadline} />
          <FieldRow label="Final Deadline" value={data.final_deadline} />
          <FieldRow label="Winners Announcement" value={data.winners_announcement_date} />
          <FieldRow label="Award Ceremony Date" value={data.award_ceremony_date} />
          <FieldRow label="Announcement Timeline" value={data.announcement_timeline} multiline />
          <FieldRow label="Submission Period" value={data.submission_period} />
          <FieldRow label="Deadline Structure" value={data.deadline_structure} multiline />

          <QcFlagDisplay
            flagged={data.qc_flag_deadlines}
            notes={data.qc_flag_deadlines_notes}
            editable={false}
          />
        </SectionBlock>

        {/* 3. REQUIREMENTS SECTION */}
        <SectionBlock title="Requirements" icon="📝">
          <FieldRow
            label="Self-Published Accepted"
            value={data.self_published_accepted ? 'Yes' : 'No'}
          />
          <FieldRow label="ISBN Required" value={data.isbn_required ? 'Yes' : 'No'} />
          <FieldRow label="Geographic Restrictions" value={data.geographic_restrictions} />
          <FieldRow
            label="Publication Date Requirements"
            value={data.publication_date_requirements}
          />
          <FieldRow label="Accepted Formats" value={data.accepted_formats} />
          <FieldRow
            label="Manuscript Requirements"
            value={
              typeof data.manuscript_requirements === 'boolean'
                ? (data.manuscript_requirements ? 'Yes' : 'No')
                : data.manuscript_requirements
            }
          />
          <FieldRow label="Deadline Type" value={data.deadline_type} />
          <FieldRow label="Requirements Summary" value={data.requirements_summary} multiline />

          <QcFlagDisplay
            flagged={data.qc_flag_requirements}
            notes={data.qc_flag_requirements_notes}
            editable={false}
          />
        </SectionBlock>

        {/* 4. FEES & PRIZES SECTION */}
        <SectionBlock title="Fees & Prizes" icon="💰">
          <FieldRow
            label="Entry Fee"
            value={
              data.entry_fee
                ? `${data.entry_fee_currency || ''} ${data.entry_fee}`
                : null
            }
          />
          <FieldRow label="Cost Range" value={data.cost_range} />
          <FieldRow label="Prizes per Category" value={data.prizes_per_category} />
          <FieldRow label="Total Win Opportunities" value={data.total_win_opportunities} />
          <FieldRow label="Has Tiered Prizes" value={data.has_tiered_prizes ? 'Yes' : 'No'} />
          <FieldRow label="Extra Benefits" value={data.extra_benefits} multiline />
          <FieldRow
            label="Prize Structure"
            value={data.prize_structure ? JSON.stringify(data.prize_structure, null, 2) : null}
            multiline
          />

          <QcFlagDisplay
            flagged={data.qc_flag_fees_prizes}
            notes={data.qc_flag_fees_prizes_notes}
            editable={false}
          />
        </SectionBlock>

        {/* 5. MEDIA SECTION */}
        <SectionBlock title="Media & URLs" icon="🌐">
          <FieldRow label="Website URL" value={data.website_url} />
          <FieldRow label="Submission URL" value={data.submission_url} />
          <FieldRow label="Past Winners URL" value={data.past_winners_url} />
          <FieldRow label="Seed URLs" value={data.seed_urls} multiline />
          <FieldRow label="Wikipedia Entry" value={data.wikipedia_entry} />

          <QcFlagDisplay
            flagged={data.qc_flag_media}
            notes={data.qc_flag_media_notes}
            editable={false}
          />
        </SectionBlock>

        {/* 6. CATEGORIES SECTION */}
        <SectionBlock title="Categories" icon="📚">
          {categories.length > 0 ? (
            <CategoryBuckets categories={categories} expandable={true} />
          ) : (
            <p className={styles.emptyText}>No categories available</p>
          )}

          <QcFlagDisplay
            flagged={data.qc_flag_categories}
            notes={data.qc_flag_categories_notes}
            editable={false}
          />
        </SectionBlock>

        {/* 7. JUDGING SECTION */}
        <SectionBlock title="Judging" icon="⚖️">
          <FieldRow label="Number of Categories" value={data.number_of_categories} />
          <FieldRow label="Judging Criteria" value={data.judging_criteria} multiline />
          <FieldRow label="Celebration Type" value={data.celebration_type} />

          <QcFlagDisplay
            flagged={data.qc_flag_judging}
            notes={data.qc_flag_judging_notes}
            editable={false}
          />
        </SectionBlock>
      </main>

      {/* Footer Navigation */}
      {navData && (
        <div className={styles.footerNav}>
          <ReportCardNavigation
            currentProgram={baciCode}
            onPrevious={handlePrevious}
            onNext={handleNext}
            hasPrevious={navData.hasPrevious}
            hasNext={navData.hasNext}
            currentIndex={navData.currentIndex}
            totalPrograms={navData.total}
          />
        </div>
      )}
    </div>
  );
}

export default ProgramReportCard;
